<template>
  <div>
    <StaticDataTable class="static-table-list">
      <template v-slot:header>
        <tr>
          <th width="30%" colspan="2"><Tooltip label="Stock thickness range this config / rule applies to. Minimum is inclusive, maximum is exclusive" helpStyle>Thickness Range</Tooltip></th>

          <th><Tooltip label="Maximum panel width for this thickness. Set to zero to use station default settings" helpStyle>Max Panel Width</Tooltip></th>
          <th><Tooltip label="Maximum panel height for this thickness. Set to zero to use station default settings" helpStyle>Max Panel Height</Tooltip></th>

          <template v-if="isCuttingOptimisationStation">
            <th><Tooltip label="Tool used for cutting primary panel rectangles on sheet and sheet trim" helpStyle>Straight Cut Tool Code</Tooltip></th>
            <th><Tooltip label="Tool used for cutting arcs within panels (eg, radius corners, circles, d-ends)" helpStyle>Shaped Cut Tool Code</Tooltip></th>
            <th><Tooltip label="Tool used for cutting lines within panels (eg, runouts, edges)" helpStyle>Shaped Open Cut Tool Code</Tooltip></th>
            <th><Tooltip label="Distance from sheet / panel edge to start/finish straight cuts" helpStyle>Edge Inset</Tooltip></th>
          </template>

          <template v-if="isWaterjetStation">
            <th><Tooltip label="Radius to apply during lead in/outs where applicable" helpStyle>Lead Radius</Tooltip></th>
            <th><Tooltip label="Minimum arc radius for arcs at the start/end of cutouts. Any arcs below this radius will not be cut" helpStyle>Min Start/End Arc Radius</Tooltip></th>
          </template>
          
          <th width="5%" v-if="editing">Actions</th>          
        </tr>
      </template>
      <template v-slot:body>
        <tr v-for="config in configList" :key="config.id">
          <td v-if="editing">
            <FormInputText type="number" v-model="config.minThickness" @input="listUpdated" prepend="Min" addon="mm" />
          </td>
          <td v-if="editing">
            <FormInputText type="number" v-model="config.maxThickness" @input="listUpdated" prepend="Max" addon="mm" />
          </td>
          <td v-if="!editing" colspan="2">{{config.minThickness}}mm - {{config.maxThickness}}mm</td>

          <td>
            <FormInputText type="number" v-if="editing" v-model="config.panelMaxWidth" @input="listUpdated" />
            <span v-if="!editing && config.panelMaxWidth > 0">{{config.panelMaxWidth}}mm</span>
            <span v-if="!editing && config.panelMaxWidth == 0" class="text-muted font-italic">Default</span>
          </td>
          <td>
            <FormInputText type="number" v-if="editing" v-model="config.panelMaxHeight" @input="listUpdated" />
            <span v-if="!editing && config.panelMaxHeight > 0">{{config.panelMaxHeight}}mm</span>
            <span v-if="!editing && config.panelMaxHeight == 0" class="text-muted font-italic">Default</span>
          </td>

          <template v-if="isCuttingOptimisationStation">
            <td>
              <FormInputText v-if="editing" v-model="config.cuttingStraightToolCode" @input="listUpdated" />
              <span v-if="!editing">{{config.cuttingStraightToolCode}}</span>
            </td>
            <td>
              <FormInputText v-if="editing" v-model="config.cuttingShapedToolCode" @input="listUpdated" />
              <span v-if="!editing">{{config.cuttingShapedToolCode}}</span>
            </td>
            <td>
              <FormInputText v-if="editing" v-model="config.cuttingShapedOpenToolCode" @input="listUpdated" />
              <span v-if="!editing">{{config.cuttingShapedOpenToolCode}}</span>
            </td>
            <td>
              <FormInputText v-if="editing" type="number" v-model="config.cuttingEdgeInsetDistance" @input="listUpdated" />
              <span v-if="!editing">{{formatDecimal(config.cuttingEdgeInsetDistance, 2)}}mm</span>
            </td>
          </template>
          <template v-if="isWaterjetStation">
            <td>
              <FormInputText v-if="editing" type="number" v-model="config.waterjetLeadRadius" @input="listUpdated" addon="mm" />
              <span v-if="!editing">{{config.waterjetLeadRadius}}</span>
            </td>
            <td>
              <FormInputText v-if="editing" type="number" v-model="config.waterjetMinRadiusStartEndArc" @input="listUpdated" addon="mm" />
              <span v-if="!editing">{{config.waterjetMinRadiusStartEndArc}}</span>
            </td>
          </template>

          <td v-if="editing">
            <ActionDropMenu :tableAction="true">
              <DropdownMenuItem @click="deleteConfig(config.id)" icon="fa-trash-alt">Delete</DropdownMenuItem>
            </ActionDropMenu>
          </td>
        </tr>
      </template>
      <template v-slot:empty v-if="configList.length == 0">
        No config items found
      </template>
    </StaticDataTable>
    <button v-if="editing" class="btn btn-white btn-xs" @click.prevent="addConfig"><i class="far fa-plus"></i> Add Config</button>
  </div>
</template>


<script>
import _ from 'lodash'
import { v4 as uuidv4 } from 'uuid'

import formMixin from '@/mixins/form'

import { ProductionStationTypes } from '@/store/types'

export default {
  name: 'StationHardwareConfigEditor',
  model: {
    event: 'update'
  },
  mixins: [formMixin],
  props: {
    editing: Boolean,
    value: Array,
    stationType: String
  },
  data() {
    return {
      configList: []
    }
  },
  computed: {
    isCuttingOptimisationStation() {
      return this.stationType == ProductionStationTypes.cuttingOptimisation
    },
    isWaterjetStation() {
      return this.stationType == ProductionStationTypes.waterjet
    },
    isTougheningStation() {
      return this.stationType == ProductionStationTypes.furnace
    },    
  },
  watch: {
    value: function() {
      this.$data.configList = _.cloneDeep(this.$props.value)
    }
  },
  mounted() {
  },
  methods: {
    addConfig() {
      this.configList.push({
        id: `temp-id-` + uuidv4(),
        isNew: true,
        minThickness: 0,
        maxThickness: 0,
        panelMaxWidth: 0,
        panelMaxHeight: 0,
        cuttingStraightToolCode: '',
        cuttingShapedToolCode: '',
        cuttingShapedOpenToolCode: '',
        cuttingEngraverToolCode: '',
        cuttingGlassIdent: '',
        cuttingEdgeInsetDistance: 0,
        waterjetLeadRadius: 0,
        waterjetMinRadiusStartEndArc: 0
      })
      this.listUpdated()
    },
    deleteConfig(id) {
      this.configList = this.configList.filter(config => config.id != id)
      this.listUpdated()
    },
    listUpdated() {
      this.$emit('update', this.configList)
    }
  }


}
</script>